import { POSITION_MAP } from 'src/components/restructure/product/shelves/GenericShelfV2/GenericShelfV2'
import type { DataRecommendation } from 'src/components/restructure/product/types/linx-recommendations'
import type { PageName } from 'src/components/restructure/product/contexts/Recommendations/recommendations-context'

import { clientStore } from '../../services/axiosInstance'
import type { DeviceInfoProps } from '../dynamicYield/getUserDeviceObject'

export type RecommendationsParams = {
  name: PageName
  source: string
  productId?: string | string[]
  userId?: string
  dysession: string
  dyuser: string
  engine?: 'dy'
  location: string
  skuId?: string | string[]
  device?: DeviceInfoProps
}

type RecommendationsDataProps = {
  pageName?: string
  categories?: string
  productId?: string
  sections: Array<{
    name: string
    data: any
  }>
}

interface Collection {
  text: string
  id: string
  engine: 'vtex' | 'linx'
}

const COLLECTION_API = '/_v3/api/decathlon-search/product/collection'
const PDP_PRODUCT_API = '/v2/api/decathlon-pdp/product'

const allSections = {
  sectionsThatUseCollectionShelf: [
    'Vitrine Editorial [Home]',
    'Vitrine Editorial [Campanha]',
    ' Vitrine - Nossos queridinhos [Departamento]',
  ],
  sectionsThatUseGenericShelf: ['Vitrine Linx [Home]', 'no-section'],
  sectionsThatUseProductIds: ['[Campanhas 2.0] Vitrine Editorial Comercial'],
}

const sectionsToCheck = [
  ...allSections.sectionsThatUseCollectionShelf,
  ...allSections.sectionsThatUseGenericShelf,
]

export async function getShelfServerSideData({
  pageName,
  productId,
  categories,
  sections,
}: RecommendationsDataProps) {
  return Promise.all(
    sections.map(async (section) => {
      if (
        allSections.sectionsThatUseProductIds.includes(section.name) &&
        section.data.enableSession
      ) {
        const shelfServerData = await getProductsByIdsData(
          section.data.products
        )

        return {
          ...section,
          data: { ...section.data, shelfServerData },
        }
      }

      if (
        !sectionsToCheck.includes(section.name) ||
        !section.data.enableSession
      ) {
        return section
      }

      const shelfServerData = pageName
        ? await getLinxShelfRecommendationData({
            pageName,
            categories,
            productId,
            position: section?.data?.position,
          })
        : await getCollectionShelfData(section.data.collections)

      return section.name === 'no-section'
        ? shelfServerData ?? []
        : {
            ...section,
            data: { ...section.data, shelfServerData: shelfServerData ?? [] },
          }
    })
  )
}

async function getLinxShelfRecommendationData({
  pageName,
  categories,
  productId,
  position,
}: {
  pageName: string
  categories?: string
  productId?: string
  position: string
}) {
  try {
    const params = new URLSearchParams()

    params.append('name', pageName)
    params.append('source', 'desktop')
    params.append('deviceId', '4484f0bc-a85c-413a-8790-dffef50fecf9')

    if (productId) {
      params.append('productId', productId)
    }

    const { data: response } = await clientStore.get(
      `/_v3/api/decathlon-search/pages/recommendations?${categories}`,
      {
        params,
      }
    )

    return response.filter((item: DataRecommendation) => {
      return (
        item.position === POSITION_MAP[position as keyof typeof POSITION_MAP] &&
        item.showcases.length
      )
    })
  } catch (error) {
    console.error({ error })

    return []
  }
}

async function getProductsByIdsData(products?: Record<string, string>) {
  if (!products) {
    return []
  }

  const ids = Object.values(products).filter(Boolean)

  if (!ids.length) {
    return []
  }

  const results = await Promise.all(
    ids.map((id) =>
      clientStore
        .get(`${PDP_PRODUCT_API}?productId=${id}`)
        .then((response) => response.data)
        .catch((error) => {
          console.error(`Erro ao buscar produto ${id}:`, error)

          return null
        })
    )
  )

  return results.filter((product) =>
    product?.items?.some((item: { isAvailable: boolean }) => item.isAvailable)
  )
}

export async function getCollectionShelfData(
  collections: Collection[],
  options?: {
    onError?: (error: unknown) => void
  }
) {
  if (!collections.length) {
    return []
  }

  try {
    const [collection] = collections

    const collectionsdata = await clientStore.get(
      `${COLLECTION_API}?collection=${collection.id}&engine=${collection.engine}&resultsPerPage=16&source=website&sc=3`
    )

    return collectionsdata?.data?.products ?? []
  } catch (error) {
    options?.onError?.(error)
    console.error({ error })

    return []
  }
}
